import { useEffect, useState } from "react";
import { IoChevronBackOutline, IoGrid } from "react-icons/io5";
import { FaList } from "react-icons/fa6";
import { ProductCards } from "../ProductCards/ProductCards";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import { useAmplitude } from "../../context/AmplitudeContext";
import { useUser } from "../../context/UserContext";
import Pagination from "../Pagination/Pagination";
import { ProductsPerPage } from "../../constants";
import { useConfig } from "../../context/ConfigContext";
import "./styles.css";

export const ProductRecommendation = ({ getRecommendations, onBackClick }) => {
  const { t } = useTranslation("recommendation");
  const trackEvent = useAmplitude();
  const { uid, selectedPreferredCategory } = useUser();
  const { logo, configData } = useConfig();

  const [products, setProducts] = useState(null);
  const [productCategories, setProductCategories] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);

  const [categoryFilter, setCategoryFilter] = useState(
    selectedPreferredCategory === null ||
      selectedPreferredCategory === "Keine" ||
      selectedPreferredCategory === "None"
      ? "All"
      : selectedPreferredCategory,
  );
  const [filteredProducts, setFilteredProducts] = useState([]);

  const [gridLayout, setGridLayout] = useState(false);

  const getPaginatedProducts = () => {
    if (products && filteredProducts) {
      const startIndex = (currentPage - 1) * 10;
      const endIndex = startIndex + 10;
      const paginated = filteredProducts.slice(startIndex, endIndex);
      return paginated;
    }
    return [];
  };

  const paginatedProducts = getPaginatedProducts();

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    getRecommendations().then((res) => {
      if (res) {
        // Flatten API response and directly filter based on userSelectedBrand
        let allBrandsProducts = Object.entries(res).flatMap(
          ([category, items]) =>
            items.map((product) => ({
              ...product,
              category: category.trim() === "" ? "All" : category, // Setting defaults to All for consistency
              brand: product.brand?.trim() === "" ? "All" : product.brand, // Setting defaults to All for consistency
            })),
        );

        // Extract unique categories & brands
        const uniqueCategories = [
          "All",
          ...new Set(allBrandsProducts.map((p) => p.category)),
        ];

        setProducts(allBrandsProducts);
        setFilteredProducts(allBrandsProducts);
        setProductCategories(uniqueCategories);
      }

      // Track user visits Results Page
      trackEvent("webapp_user_visited_products_page", uid);
      trackEvent("webapp_user_visited_last_page", uid, "products");
    });
  }, [getRecommendations, uid, trackEvent, selectedPreferredCategory]);

  useEffect(() => {
    if (products) {
      const filteredProducts = products.filter(
        (product) =>
          categoryFilter === "All" ||
          product.category.toLowerCase() === categoryFilter.toLowerCase(),
      );
      setCurrentPage(1);
      setFilteredProducts(filteredProducts);
    }
  }, [categoryFilter, products]);

  return (
    <div className="product-recommend">
      <div className="logo-bar">
        <div className="products-back-button-wrapper">
          <IoChevronBackOutline
            className="back-icon"
            onClick={() => onBackClick()}
          />
        </div>
        {configData?.ProductRecommendationFlag?.logoEnabled !== false ? (
          <img
            className="logo-img"
            src={
              configData?.ProductRecommendationFlag?.logoUrl
                ? configData?.ProductRecommendationFlag?.logoUrl
                : logo
            }
            alt="Logo"
          />
        ) : null}
        <div className="products-layout-switching-wrapper">
          {!gridLayout ? (
            <IoGrid
              className="layout-icon"
              onClick={() => setGridLayout(true)}
            />
          ) : (
            <FaList
              className="layout-icon"
              onClick={() => setGridLayout(false)}
            />
          )}
        </div>
      </div>
      <div className="recommendation-scroll-content">
        <div className="product-page_header">
          <h4 className="products-page-info">{t("title-1")}</h4>
          <h4 className="products-page-info">{t("title-2")}</h4>
        </div>
        {products ? (
          <>
            {/* Category Filter */}
            {productCategories.length > 2 && (
              <>
                <p className="small-text">{t("filter-by-categories")}</p>
                <div className="products-filter-wrapper">
                  {productCategories.map((categoryName, index) => (
                    <h4
                      key={categoryName + index}
                      className={`product-filter-title ${categoryFilter.toLowerCase() === categoryName.toLowerCase() ? "product-filter-title_selected" : ""}`}
                      onClick={() => setCategoryFilter(categoryName)}
                    >
                      {categoryName === "All" ? t("All") : categoryName}
                    </h4>
                  ))}
                </div>
              </>
            )}

            {/* Products */}
            <div className={`products-wrapper${gridLayout ? "_grid" : ""}`}>
              {paginatedProducts.length > 0 ? (
                <ProductCards
                  layout={gridLayout ? "grid" : "list"}
                  products={paginatedProducts}
                />
              ) : (
                <div className="not-found-container">
                  <h4 className="not-found-header">{t("not-found-header")}</h4>
                  <p className="not-found-text">{t("not-found-text")}</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {filteredProducts.length > ProductsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={
                  ProductsPerPage > 0
                    ? Math.ceil(filteredProducts.length / ProductsPerPage)
                    : 1
                }
                onPageChange={handlePageChange}
              />
            )}
          </>
        ) : (
          <div
            style={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "70vh",
              color: "#000",
            }}
          >
            <CircularProgress color="inherit" />
          </div>
        )}
      </div>
    </div>
  );
};
